import { memo } from "react"
import { TriangleAlert } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import type { IParcel } from "@/types/explore"
import { formatNumber } from "@/lib/explore/utils/format-helpers"

export const KeyIndicators = memo(
  ({
    selectedParcel,
    elevation,
    ownerType,
    communeLabel,
  }: {
    selectedParcel: IParcel
    elevation: number | null
    ownerType: string | null
    communeLabel?: string | null
  }) => {
    const surface = selectedParcel.properties?.contenance
    const commune = selectedParcel.properties?.commune_name || selectedParcel.properties?.nom_com
    const inseeCode = selectedParcel.properties?.idu?.substring(0, 5)
    const isCommuneMismatch = commune && communeLabel && commune !== communeLabel
    const cells: { label: React.ReactNode; value: React.ReactNode }[] = []

    if (commune || inseeCode)
      cells.push({
        label: (
          <span className="flex items-center gap-1">
            Commune
            {isCommuneMismatch && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 rounded bg-amber-100 text-amber-600">
                      <TriangleAlert className="w-3 h-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Cette parcelle n'appartient pas a la commune selectionnee</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        ),
        value: `${commune || ""}${inseeCode ? ` (${inseeCode})` : ""}`,
      })
    if (surface != null)
      cells.push({
        label: "Surface",
        value: `${formatNumber(Math.round(surface))} m\u00B2`,
      })
    if (elevation !== null) cells.push({ label: "Alt.", value: `${Math.round(elevation)}m` })
    if (ownerType) cells.push({ label: "Proprio.", value: ownerType })

    if (cells.length === 0) return null

    return (
      <div className="flex rounded-md overflow-hidden border border-gray-100">
        {cells.map((cell, i) => (
          <div key={i} className={`flex-1 px-2 py-1.5${i < cells.length - 1 ? " border-r border-gray-100" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">{cell.label}</div>
            <div className="text-sm font-bold text-gray-800 whitespace-nowrap">{cell.value}</div>
          </div>
        ))}
      </div>
    )
  },
)
